/**
 * SyncKeyWatcher — the ONE place background-sync status polls come from.
 *
 * A batch cell response reports every constituent's in-flight server syncs
 * as poll keys (`GET /api/sync/{key}/status`). On a cold account with many
 * calendars that is dozens of keys (viewport + backfill per calendar, per
 * fetched range), reported again by every aggregate, every hydrated sheet,
 * and every refetch. Polling each report independently is what drove the
 * cold-session storm of 2026-09-24: hundreds of status GETs a minute, the
 * per-user limit (2000 req / 60s) exhausted, every later request — cell
 * writes included — answered 429.
 *
 * So polling is owned here, per `SyncPoll` capability (the accessor threads
 * ONE poll function into every sheet it hydrates, so every sheet in the
 * client shares one watcher):
 *
 *   - one poll loop per sync key, however many callers `watch` it; they all
 *     await the same outcome, and the loop sends the key's next poll only
 *     after the last one settled, so a key has at most one poll out;
 *   - per-key exponential backoff (500ms, ×1.5, capped at 5s);
 *   - a client-wide pace: at most one status GET per `MIN_POLL_SPACING_MS`,
 *     so the poll rate is bounded no matter how many keys are in flight;
 *   - a 429 pauses EVERY poll (the limit is per user, not per key) for the
 *     server's `Retry-After` when known, otherwise an exponential pause;
 *   - a poll unanswered after `POLL_TIMEOUT_MS` is aborted and retried on the
 *     key's backed-off cadence. A timeout is transient, not a failure, and is
 *     not reported per poll: the first timeout of a streak warns once, and
 *     the first poll to settle after it ends the streak with one
 *     `batch-sync/poll-recovered` breadcrumb;
 *   - every `watch` is a lease (`SyncKeyWatcher.Lease`): the key's loop
 *     counts its unreleased leases, and when the last one is released (a
 *     disposed sheet, a disposed client) it stops at once — its sleep and
 *     its slot wait cut short, a poll in flight aborted — so a sheet nobody
 *     shows spends none of the per-user request budget.
 */

import type { Option } from "../core/option.ts";
import type { Unsubscribe } from "../backends/rows-backend.ts";
import { Tracing } from "@tento-core/tracing";

import { type SyncPoll, SyncPollThrottledError, type SyncStatus } from "./sync-state.ts";
import { type SyncKey } from "../ids/ids.ts";

/**
 * How a watched key ended: terminal on the server, given up on, or
 * `"released"` — the lease was released (or the watcher disposed) first.
 */
export type SyncWatchOutcome = "complete" | "failed" | "expired" | "released";

/** One key's poll loop and the leases holding it. */
type KeyLoop = {
  readonly outcome: Promise<SyncWatchOutcome>;
  /** Stopped when the last lease is released: ends the loop. */
  readonly stop: LoopStop;
  /** The key's last answered status (`null` before its first answer). */
  readonly heard: { status: Option<SyncStatus> };
  holders: number;
};

/** How one status poll ended. */
type PollAnswer =
  | { readonly kind: "answered"; readonly status: SyncStatus }
  | { readonly kind: "failed"; readonly error: unknown }
  | { readonly kind: "timed-out" };

/**
 * Polls the server's background-sync status keys for a client, each key once however often it is
 * reported, with backoff.
 */
export class SyncKeyWatcher {
  /** Delay before a key's first poll; each further poll waits ×`BACKOFF`. */
  static readonly INITIAL_DELAY_MS = 500;
  static readonly BACKOFF = 1.5;
  static readonly MAX_DELAY_MS = 5_000;
  /** Polls per key before giving up (~5 minutes at the capped delay). */
  static readonly MAX_ATTEMPTS = 60;
  /** Client-wide floor between two status GETs: at most 8 per second. */
  static readonly MIN_POLL_SPACING_MS = 125;
  /** Pause after a 429 with no `Retry-After`; doubles per repeat, capped. */
  static readonly THROTTLE_BASE_MS = 5_000;
  static readonly THROTTLE_MAX_MS = 60_000;
  /**
   * How long one status poll may go unanswered before it is aborted: two
   * capped poll intervals (10s). A status answer is only worth its freshness,
   * and one older than the next two polls the loop would have sent is stale;
   * a healthy server answers in well under a second. It also keeps an
   * abandoned poll inside the 20s single-request budget the UX specs hold a
   * request to, where a hung one ran until the browser gave up (60-99s).
   * The timer is a `setTimeout`, so a test's fake clock drives it.
   */
  static readonly POLL_TIMEOUT_MS = 2 * SyncKeyWatcher.MAX_DELAY_MS;

  static readonly #shared = new WeakMap<SyncPoll, SyncKeyWatcher>();

  /** The watcher shared by every caller holding the same poll capability. */
  static for(poll: SyncPoll): SyncKeyWatcher {
    let watcher = SyncKeyWatcher.#shared.get(poll);
    if (!watcher) {
      watcher = new SyncKeyWatcher(poll);
      SyncKeyWatcher.#shared.set(poll, watcher);
    }
    return watcher;
  }

  readonly #inFlight = new Map<string, KeyLoop>();
  /** Set by `dispose`: every loop stopped, every later `watch` released. */
  #disposed = false;
  /** Per-key observers of non-terminal status changes (`watch`'s `onStatus`). */
  readonly #statusListeners = new Map<
    string,
    Set<(status: SyncStatus) => void>
  >();
  /** Wall time of the next free client-wide poll slot. */
  #nextSlotAt = 0;
  /** Every poll waits until this wall time (set by a 429). */
  #pausedUntil = 0;
  /** Consecutive 429 pauses, for the exponential pause length. */
  #throttleStreak = 0;
  /** Polls timed out since a poll last settled on its own; `0` when none. */
  #timeoutStreak = 0;
  /** Wall time of the streak's first timeout. */
  #timeoutStreakSince = 0;

  readonly #poll: SyncPoll;
  constructor(poll: SyncPoll) {
    this.#poll = poll;
  }

  /**
   * Poll `syncKey` to a terminal state, held by the returned lease. A key
   * already being watched is not polled again: the lease shares the
   * in-flight loop's outcome. Release the lease when its holder no longer
   * needs the key (`Sheet.dispose` releases its own): its `outcome`
   * resolves `"released"` at once, and when it was the key's last lease the
   * loop stops — no further status GET, its timers cleared, a poll in
   * flight aborted. On a disposed watcher the lease is released already
   * and nothing is polled.
   *
   * `onStatus` observes the key's status as it changes while the loop runs
   * (`pending` → `running` …), for callers that show per-row progress (the
   * legacy row-handle sync state) and for a probe that asks whether a key's
   * job is live (`SheetCellLoads`). An observer joining a loop that already
   * answered a non-terminal status hears that status at once, before `watch`
   * returns: a change it missed is still the key's state (a probe joining a
   * new job's loop after its `pending` would otherwise read the job as the
   * finished one). It is dropped when the loop ends or the lease is
   * released. An observer that throws on that replay throws out of `watch`
   * with its lease already released, so it holds the loop no longer.
   */
  watch(
    syncKey: SyncKey,
    onStatus: Option<(status: SyncStatus) => void>,
  ): SyncKeyWatcher.Lease {
    if (this.#disposed) return RELEASED_LEASE;
    const observer = onStatus == null ? null : (status: SyncStatus): void => onStatus(status);
    let leaseListeners: Option<Set<(status: SyncStatus) => void>> = null;
    if (observer) {
      let listeners = this.#statusListeners.get(syncKey);
      if (!listeners) {
        listeners = new Set();
        this.#statusListeners.set(syncKey, listeners);
      }
      listeners.add(observer);
      leaseListeners = listeners;
    }
    let loop = this.#inFlight.get(syncKey);
    if (!loop) {
      const stop = new LoopStop();
      const heard: KeyLoop["heard"] = { status: null };
      const started: KeyLoop = {
        outcome: this.#run(syncKey, stop, heard).finally(() => {
          if (this.#inFlight.get(syncKey) !== started) return;
          this.#inFlight.delete(syncKey);
          this.#statusListeners.delete(syncKey);
        }),
        stop,
        heard,
        holders: 0,
      };
      this.#inFlight.set(syncKey, started);
      loop = started;
    }
    const held = loop;
    held.holders++;
    let released = false;
    let onRelease: (outcome: SyncWatchOutcome) => void = () => {};
    const whenReleased = new Promise<SyncWatchOutcome>((resolve) => {
      onRelease = resolve;
    });
    const release = (): void => {
      if (released) return;
      released = true;
      if (observer) leaseListeners?.delete(observer);
      onRelease("released");
      held.holders--;
      if (held.holders > 0 || this.#inFlight.get(syncKey) !== held) return;
      this.#inFlight.delete(syncKey);
      this.#statusListeners.delete(syncKey);
      held.stop.stop();
    };
    const replay = held.heard.status;
    if (observer && (replay === "pending" || replay === "running")) {
      try {
        observer(replay);
      } catch (e) {
        release();
        throw e;
      }
    }
    return {
      outcome: Promise.race([held.outcome, whenReleased]),
      release,
      [Symbol.dispose]: release,
    };
  }

  /** Keys with a poll loop running (each held by at least one lease). */
  get watchedKeys(): number {
    return this.#inFlight.size;
  }

  /**
   * Stop every key's loop and release every lease (the owning client's
   * `dispose`): no status GET is sent afterwards, and a later `watch`
   * returns a released lease. Idempotent.
   */
  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    const loops = [...this.#inFlight.values()];
    this.#inFlight.clear();
    this.#statusListeners.clear();
    for (const loop of loops) loop.stop.stop();
  }

  async #run(syncKey: SyncKey, stop: LoopStop, heard: KeyLoop["heard"]): Promise<SyncWatchOutcome> {
    Tracing.log("batch-sync/poll-start", { syncKey });
    let outcome: SyncWatchOutcome = "expired";
    let delay = SyncKeyWatcher.INITIAL_DELAY_MS;
    let attempt = 0;
    let timeouts = 0;
    try {
      for (; attempt < SyncKeyWatcher.MAX_ATTEMPTS; attempt++) {
        await sleep(delay, stop);
        delay = Math.min(delay * SyncKeyWatcher.BACKOFF, SyncKeyWatcher.MAX_DELAY_MS);
        if (!stop.stopped) await this.#acquireSlot(stop);
        if (stop.stopped) {
          outcome = "released";
          return outcome;
        }
        const answer = await this.#pollOnce(syncKey, stop);
        if (stop.stopped) {
          outcome = "released";
          return outcome;
        }
        if (answer.kind === "timed-out") {
          timeouts++;
          this.#noteTimeout(syncKey);
          continue; // transient — retry on the backed-off cadence
        }
        this.#noteSettled();
        if (answer.kind === "failed") {
          if (answer.error instanceof SyncPollThrottledError) {
            this.#pauseAfterThrottle(answer.error.retryAfterMs, { syncKey });
          }
          continue; // transient failure — retry on the backed-off cadence
        }
        const status = answer.status;
        this.#throttleStreak = 0;
        if (status !== heard.status) {
          heard.status = status;
          Tracing.log("batch-sync/poll-status", { syncKey, status, attempt });
          for (const listener of this.#statusListeners.get(syncKey) ?? []) {
            listener(status);
          }
        }
        if (status === "complete" || status === "failed") {
          outcome = status;
          return outcome;
        }
        // pending / running → keep polling
      }
      return outcome;
    } finally {
      Tracing.log("batch-sync/poll-end", { syncKey, outcome, attempts: attempt, timeouts });
    }
  }

  /**
   * One status poll of `syncKey`, aborted if it is still out after
   * `POLL_TIMEOUT_MS`. It waits for the poll to settle even then (a `SyncPoll`
   * rejects once its signal aborts), so the key never has two polls out.
   */
  async #pollOnce(syncKey: SyncKey, stop: LoopStop): Promise<PollAnswer> {
    const controller = new AbortController();
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, SyncKeyWatcher.POLL_TIMEOUT_MS);
    // The loop's last lease released: the poll in flight is aborted too.
    const unwatchStop = stop.onStop(() => controller.abort());
    try {
      const response = await this.#poll(syncKey, controller.signal);
      return { kind: "answered", status: response?.status ?? "complete" };
    } catch (error) {
      return timedOut ? { kind: "timed-out" } : { kind: "failed", error };
    } finally {
      clearTimeout(timer);
      unwatchStop();
    }
  }

  /** A poll timed out: the first of a streak warns, once. */
  #noteTimeout(syncKey: SyncKey): void {
    if (this.#timeoutStreak++ > 0) return;
    this.#timeoutStreakSince = Date.now();
    Tracing.warn("net")("sync status polls timing out; retrying each on its cadence", {
      syncKey,
      timeoutMs: SyncKeyWatcher.POLL_TIMEOUT_MS,
      inFlightKeys: this.#inFlight.size,
    });
  }

  /** A poll settled on its own (answered or refused): a timeout streak ends. */
  #noteSettled(): void {
    if (this.#timeoutStreak === 0) return;
    Tracing.log("batch-sync/poll-recovered", {
      timedOut: this.#timeoutStreak,
      outageMs: Date.now() - this.#timeoutStreakSince,
    });
    this.#timeoutStreak = 0;
  }

  /**
   * Wait for a client-wide poll slot. Slots are reserved in call order, one
   * per `MIN_POLL_SPACING_MS`, never before `pausedUntil`; a 429 landing
   * while a caller waits pushes it past the new pause.
   */
  async #acquireSlot(stop: LoopStop): Promise<void> {
    for (;;) {
      const now = Date.now();
      const at = Math.max(now, this.#nextSlotAt, this.#pausedUntil);
      this.#nextSlotAt = at + SyncKeyWatcher.MIN_POLL_SPACING_MS;
      if (at > now) await sleep(at - now, stop);
      if (stop.stopped || Date.now() >= this.#pausedUntil) return;
    }
  }

  /**
   * Another request of this client was answered 429 (e.g. a cells batch).
   * The limit is per user, so pause every status poll for the same window,
   * as a poll's own 429 does. Returns how long from now the pause lasts, for
   * the caller to wait out before it retries.
   */
  throttled(retryAfterMs: Option<number>, source: string): number {
    this.#pauseAfterThrottle(retryAfterMs, { source });
    return Math.max(0, this.#pausedUntil - Date.now());
  }

  #pauseAfterThrottle(
    retryAfterMs: Option<number>,
    cause: Readonly<Record<string, string>>,
  ): void {
    const now = Date.now();
    // Polls already in flight when the first 429 landed answer 429 too —
    // that is the same over-budget window, not a new one.
    if (now < this.#pausedUntil) return;
    // A 429 long after the last pause ended starts a new streak: a successful
    // poll resets it, but a client whose only 429s come from `throttled`
    // (no poll in between) would otherwise keep doubling across sessions.
    if (now - this.#pausedUntil > SyncKeyWatcher.THROTTLE_MAX_MS) {
      this.#throttleStreak = 0;
    }
    const backoff = Math.min(
      SyncKeyWatcher.THROTTLE_BASE_MS * 2 ** this.#throttleStreak,
      SyncKeyWatcher.THROTTLE_MAX_MS,
    );
    this.#throttleStreak++;
    const pauseMs = retryAfterMs ?? backoff;
    this.#pausedUntil = now + pauseMs;
    Tracing.warn("net")("sync status polls paused after 429", {
      ...cause,
      pauseMs,
      retryAfter: retryAfterMs !== null,
      inFlightKeys: this.#inFlight.size,
    });
  }
}

/**
 * Types that go with `SyncKeyWatcher`: `Lease`.
 */
export namespace SyncKeyWatcher {
  /**
   * One holder's claim on a watched key (`SyncKeyWatcher.watch`). Release it
   * when the key no longer matters to its holder; the key's loop stops when
   * its last lease is released. Also `[Symbol.dispose]`, so a script writes
   * `using lease = watcher.watch(key, null);`.
   */
  export interface Lease {
    /** How the key ended — `"released"` once this lease was released first
     *  (or the watcher was disposed). Never rejects. */
    readonly outcome: Promise<SyncWatchOutcome>;
    /** Release this lease; a second call changes nothing. */
    release(): void;
    /** `release`, for `using`. */
    [Symbol.dispose](): void;
  }
}

/** The lease a disposed watcher hands out: released already. */
const RELEASED_LEASE: SyncKeyWatcher.Lease = Object.freeze({
  outcome: Promise.resolve<SyncWatchOutcome>("released"),
  release: () => {},
  [Symbol.dispose]: () => {},
});

/** A key loop's stop switch: flipped once, running each waiter's callback. */
class LoopStop {
  #stopped = false;
  readonly #waiters = new Set<() => void>();

  get stopped(): boolean {
    return this.#stopped;
  }

  stop(): void {
    if (this.#stopped) return;
    this.#stopped = true;
    const waiters = [...this.#waiters];
    this.#waiters.clear();
    for (const waiter of waiters) waiter();
  }

  /** Run `waiter` on `stop` (at once when stopped already); returns its removal. */
  onStop(waiter: () => void): Unsubscribe {
    if (this.#stopped) {
      waiter();
      return () => {};
    }
    this.#waiters.add(waiter);
    return () => {
      this.#waiters.delete(waiter);
    };
  }
}

/** Wait `ms`, or less when `stop` flips (the timer then cleared). */
function sleep(ms: number, stop: LoopStop): Promise<void> {
  if (stop.stopped) return Promise.resolve();
  return new Promise((resolve) => {
    let unwatch: Unsubscribe = () => {};
    const timer = setTimeout(() => {
      unwatch();
      resolve();
    }, ms);
    unwatch = stop.onStop(() => {
      clearTimeout(timer);
      resolve();
    });
  });
}
